import * as _ from 'underscore';
import { ResponsiveService } from '../responsive/responsive.service';
import { IExtendedDocument, IObject } from '../../../type_definitions/datatypes';
import { Inject, Injectable } from '@angular/core';
import { util } from '../util';
import { DOCUMENT } from '@angular/common';

interface IScrollOptions {
  duration?:number;
}

/**
 * @ngdoc service
 * @name ga2.shared.scroll:scrollService
 *
 * @description The scrollService enables us to let the window scroll to a html element that has a
 *    {@link ga2.common.directive:scrollTarget} directive.
 */

const SCROLL_OPTIONS:IScrollOptions = {
  duration: 400
};

@Injectable()
export class ScrollService {
  private scrollWidth:number;
  private scrollHeight:number;
  private currentScrollTarget:string;
  private scrollers:IObject = {};

  constructor(
    @Inject(DOCUMENT) private document:IExtendedDocument,
    private responsiveService:ResponsiveService
  ) {
    /***/
  }

  /**
   * @ngdoc method
   * @name ga2.shared.scroll:scrollService#registerScrollTarget
   * @methodOf ga2.shared.scroll:scrollService
   * @description Add a target that can be scrolled to
   * @param {string} scrollTarget the name of the target that can be scrolled to
   * @param {Function} scroller a function that can be used to reach this scrollTarget
   */
  public registerScrollTarget = (scrollTarget:string, scroller:{ scroll:() => void }):void => {
    this.scrollers[ scrollTarget ] = scroller;
  };

  /**
   * @ngdoc method
   * @name ga2.shared.scroll:scrollService#unregisterScrollTarget
   * @methodOf ga2.shared.scroll:scrollService
   * @description Remove the scrollTarget from the list
   * @param {string} scrollTarget the name of the target
   */
  public unregisterScrollTarget = (scrollTarget:string):void => {
    delete this.scrollers[ scrollTarget ];
  };


  /**
   * @ngdoc method
   * @name ga2.shared.scroll:scrollService#getCurrentTarget
   * @methodOf ga2.shared.scroll:scrollService
   * @description The target that last received a scrollTo request
   * @returns {string} the current scrollTarget
   */
  public getCurrentTarget = ():string => this.currentScrollTarget;

  public clear = () => {
    this.currentScrollTarget = undefined;
  };

  /**
   * @ngdoc method
   * @name ga2.shared.scroll:scrollService#scrollTo
   * @methodOf ga2.shared.scroll:scrollService
   * @description Scroll to a named target (will be remembered when it is not registered yet)
   * @param {string} scrollTarget the name of the target
   */
  public scrollTo = (scrollTarget:string):void => {
    console.debug('scrollTo: ' + scrollTarget);
    !scrollTarget || !this.scrollers[ scrollTarget ] || this.scrollers[ scrollTarget ].scroll();
    this.currentScrollTarget = scrollTarget;
  };

  /**
   * @ngdoc method
   * @name ga2.shared.scroll:scrollService#scrollToElement
   * @methodOf ga2.shared.scroll:scrollService
   * @description Scroll to a named class (will be remembered when it is not registered yet)
   *
   * @param {string|element} selector a jquery selector or an element
   * @param {=number} extraOffset extra offset that has to be added (static top navigation is automatically added)
   * @param {boolean} removeDefaultOffset will remove predefined offset
   */
  public scrollToElement = (selector:string | JQuery, extraOffset?:number, removeDefaultOffset?:boolean):void => {
    const scrollTarget:JQuery = $(selector);
    if (scrollTarget.length) {
      let offset:number = extraOffset || 0;
      // if 'removeDefaultOffset' argument is not defined or 'false' then add predefined offset calculation
      if (!removeDefaultOffset) {
        const wizardFixedTopContainerHeight:number = $('.fixed-to-top-container').outerHeight(true) || 0;
        const collapsibleSectionsHeaderHeight:number = $('.collapsible-sections > h2').outerHeight(true) || 0;
        offset += this.responsiveService.getFixedHeaderHeight() + wizardFixedTopContainerHeight + collapsibleSectionsHeaderHeight;
      }
      const scrollParent:JQuery<HTMLElement> = $(this.document).find('#ga2-content');
      const targetOffset:number = scrollParent.is(scrollTarget) ? 0 : (scrollParent.scrollTop() + scrollTarget.offset().top);
      // if target is #ga2-content or content-top, do not animate as it can cause weird scrolling behavior in wizard
      const scrollDuration:number = !targetOffset ? 0 : SCROLL_OPTIONS.duration;
      scrollParent.animate({ scrollTop: targetOffset + offset }, scrollDuration);
    }
  };

  public scrollTop(top?:number):number {
    const contentFrameElement:JQuery<HTMLElement> = $(this.document).find('#ga2-content');
    if (util.notNullOrUndefined(top)) {
      contentFrameElement.animate({ scrollTop: top }, SCROLL_OPTIONS.duration);
    }
    return contentFrameElement.scrollTop();
  }

  public scrollModalTop = (top:number):number => {
    const modalElement:JQuery<HTMLElement> = $('.modal');
    if (util.notNullOrUndefined(top)) {
      modalElement.scrollTop(top);
    }
    return modalElement.scrollTop();
  };

  public scrollbarWidth = ():number => {
    if (_.isUndefined(this.scrollWidth)) {
      this.calcScrollbarDimensions();
    }
    return this.scrollWidth;
  };

  public scrollbarHeight = ():number => {
    if (_.isUndefined(this.scrollHeight)) {
      this.calcScrollbarDimensions();
    }
    return this.scrollHeight;
  };

  public horizontalScrollbarShowing = element => {
    element = element.get? element.get(0) : element; //works for jQuery too
    return element.clientWidth < element.scrollWidth;
  };

  public verticalScrollbarShowing = element => {
    element = element.get? element.get(0) : element; //works for jQuery too
    return element?.clientHeight < element?.scrollHeight;
  };

  private calcScrollbarDimensions():void {
    //add a 100% by 100% div within a 100px by 100px div, set overflow to scroll, and measure difference in
    // width and height

    const outer:HTMLDivElement = this.document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    outer.style.height = '100px';
    (<any>outer.style).msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    this.document.body.appendChild(outer);

    const widthNoScroll:number = outer.offsetWidth;
    const heightNoScroll:number = outer.offsetHeight;

    // force scrollbars
    outer.style.overflow = 'scroll';
    // add inner DIV
    const inner:HTMLDivElement = this.document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '100%';
    outer.appendChild(inner);
    const widthWithScroll:number = inner.offsetWidth;
    const heightWithScroll:number = inner.offsetHeight;

    // remove divs
    outer.parentNode.removeChild(outer);

    this.scrollWidth = widthNoScroll - widthWithScroll;
    this.scrollHeight = heightNoScroll - heightWithScroll;
  }
}